import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { sendDayAIChat } from '@/lib/api';
import { Sparkles, Send, Loader2, X, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function DayAIChatModal({ dayPlanId, open, onClose }) {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  // Generate unique session ID when modal opens
  useEffect(() => {
    if (open && !sessionId) {
      const newSessionId = `replanning_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSessionId);
      console.log('New chat session created:', newSessionId);
    }
  }, [open]);

  // Reset conversation when modal closes
  useEffect(() => {
    if (!open) {
      setMessages([]);
      setSessionId(null);
    }
  }, [open]);

  const handleSend = async () => {
    if (!message.trim() || !sessionId) return;

    const userMsg = message;
    setMessage('');
    
    // Add user message to UI
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      console.log('Sending message with session:', sessionId);
      const res = await sendDayAIChat(dayPlanId, userMsg, sessionId);
      
      // Add AI response
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      
      console.log('Response received. Session persisted:', res.data.session_id);
    } catch (error) {
      toast.error('Erreur du chat IA');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl h-[600px] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold">Assistant IA Planificateur</DialogTitle>
                <p className="text-xs text-gray-500">Optimisez votre journée avec l'IA</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="rounded-full w-8 h-8 p-0">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <MessageCircle className="w-16 h-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">Commencez la conversation</h3>
              <p className="text-sm text-gray-500 max-w-md">
                Demandez à l'IA de vous aider à optimiser votre journée, ajouter des activités, 
                ajuster les horaires, ou poser des questions sur votre itinéraire.
              </p>
              <div className="mt-6 space-y-2">
                <p className="text-xs text-gray-400">Exemples de questions :</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {[
                    "Ajoute une pause café à 15h",
                    "Optimise les trajets",
                    "Qu'est-ce qu'on fait ce soir ?",
                  ].map((example, i) => (
                    <button
                      key={i}
                      onClick={() => setMessage(example)}
                      className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  msg.role === 'user' 
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))
          )}
          
          {loading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-2xl px-4 py-3 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                <span className="text-sm text-gray-600">L'IA réfléchit...</span>
              </div>
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="px-6 py-4 border-t border-gray-100">
          <div className="flex gap-2">
            <Input
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Posez votre question à l'IA..."
              className="flex-1 rounded-xl"
              disabled={loading}
            />
            <Button
              onClick={handleSend}
              disabled={loading || !message.trim()}
              className="rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-xs text-gray-400 mt-2">Appuyez sur Entrée pour envoyer</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
